import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { useTranslation } from '@/i18n'
import { REVIEW_THRESHOLD_MIN, REVIEW_THRESHOLD_MAX } from './reviewLayer'

export interface ReviewItem {
  id: string
  label: string
  confidence?: number
  // SigLIP trust (VISN-291): the score the flagging used when present.
  trust?: number
}

interface ReviewControlsProps {
  /** Current confidence cutoff (0–1); items below it are flagged. */
  threshold: number
  onThresholdChange: (threshold: number) => void
  /** When true, non-flagged detections keep their per-class colors instead of the neutral mute. */
  fullColor: boolean
  onFullColorChange: (fullColor: boolean) => void
  symbolsOnly: boolean
  onSymbolsOnlyChange: (symbolsOnly: boolean) => void
  /** Flagged items for this sheet, in reading order. */
  items: ReviewItem[]
  /** Index of the item the canvas is currently focused on (−1 = none). */
  currentIndex: number
  /** Jump the canvas to the flagged item at this index. */
  onJump: (index: number) => void
  /** Step the canvas to the previous / next flagged item (wraps). */
  onPrev: () => void
  onNext: () => void
}

/**
 * Floating controls for the Review layer (PRD "Vision HITL Labeling"):
 * a per-sheet "N items need review" counter with next/prev stepping, a threshold
 * slider that re-filters the flagged set live, and a "full color" toggle that
 * restores the muted Detected boxes to their per-class palette.
 *
 * Rendered by EditorWrapper only while the Review layer is active.
 */
export default function ReviewControls({
  threshold,
  onThresholdChange,
  fullColor,
  symbolsOnly,
  onSymbolsOnlyChange,
  onFullColorChange,
  items,
  currentIndex,
  onJump,
  onPrev,
  onNext,
}: ReviewControlsProps) {
  const { t } = useTranslation()
  const pct = Math.round(threshold * 100)
  const reviewCount = items.length
  const hasItems = reviewCount > 0

  return (
    <div className="absolute top-[4.5rem] left-4 z-40 flex w-64 flex-col gap-3 rounded-xl border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card p-3 shadow-md">
      {/* Counter + next/prev queue stepping */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          {t('data_catalog:graph_editor.review.count', {
            n: reviewCount,
            defaultValue: '{{n}} items need review',
          })}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasItems}
            aria-label={t('data_catalog:graph_editor.review.previous', { defaultValue: 'Previous item' })}
            className="rounded-md p-1 text-foreground hover:bg-black/5 dark:hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasItems}
            aria-label={t('data_catalog:graph_editor.review.next', { defaultValue: 'Next item' })}
            className="rounded-md p-1 text-foreground hover:bg-black/5 dark:hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Clickable queue — each item pans the canvas to it (PRD "queue, not just a map") */}
      {hasItems && (
        <ul className="max-h-40 overflow-y-auto rounded-md border border-[#e5e5e5] dark:border-dark-border">
          {items.map((item, index) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onJump(index)}
                aria-current={index === currentIndex || undefined}
                className={`flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-xs transition-colors ${
                  index === currentIndex
                    ? 'bg-[#FF6A00]/10 text-foreground'
                    : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                <span className="truncate">{item.label}</span>
                {(item.trust ?? item.confidence) != null && (
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {Math.round((item.trust ?? item.confidence)! * 100)}%
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Threshold slider — re-filters the Review layer live off confidence */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label htmlFor="review-threshold" className="text-xs font-medium text-muted-foreground">
            {t('data_catalog:graph_editor.review.flagBelow', { defaultValue: 'Flag below' })}
          </label>
          <span className="text-xs font-medium tabular-nums text-foreground">{pct}%</span>
        </div>
        <input
          id="review-threshold"
          type="range"
          min={Math.round(REVIEW_THRESHOLD_MIN * 100)}
          max={Math.round(REVIEW_THRESHOLD_MAX * 100)}
          step={1}
          value={pct}
          onChange={(e) => onThresholdChange(Number(e.target.value) / 100)}
          aria-label={t('data_catalog:graph_editor.review.threshold', { defaultValue: 'Review confidence threshold' })}
          className="w-full cursor-pointer accent-[#FF6A00]"
        />
      </div>

      {/* Symbol-classification review by default; text (PyMuPDF-deterministic)
          joins the queue only when toggled in (VISN-291). */}
      <div className="flex items-center justify-between">
        <label htmlFor="review-symbols-only" className="text-xs font-medium text-muted-foreground">
          {t('data_catalog:graph_editor.review.symbolsOnly', { defaultValue: 'Symbols only' })}
        </label>
        <Switch
          id="review-symbols-only"
          checked={symbolsOnly}
          onCheckedChange={onSymbolsOnlyChange}
          aria-label={t('data_catalog:graph_editor.review.symbolsOnlyToggle', { defaultValue: 'Review symbols only' })}
        />
      </div>

      {/* Restore the muted Detected boxes to full per-class color */}
      <div className="flex items-center justify-between">
        <label htmlFor="review-full-color" className="text-xs font-medium text-muted-foreground">
          {t('data_catalog:graph_editor.review.fullColor', { defaultValue: 'Show detected in full color' })}
        </label>
        <Switch
          id="review-full-color"
          checked={fullColor}
          onCheckedChange={onFullColorChange}
          aria-label={t('data_catalog:graph_editor.review.fullColorToggle', { defaultValue: 'Restore full color' })}
        />
      </div>
    </div>
  )
}
